import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { 
  addDays, addMonths, addWeeks, format, isSameDay, isSameMonth, 
  startOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, eachDayOfInterval 
} from "date-fns";
import { 
  ChevronLeft, ChevronRight, Plus, Calendar as CalendarIcon, 
  Clock, User, CalendarDays, ExternalLink
} from "lucide-react";
import { cn } from "../../lib/utils";
import { Modal } from "../../components/ui/modal";
import { DateTimePicker } from "../../components/ui/date-picker";
import { usePersistentState } from "../../hooks/use-persistent-state";
import { crmApi } from "./api";
import type { CrmAppointment, Lead } from "./types";
import {
  Select as _Select, SelectContent as _SelectContent, SelectItem as _SelectItem,
  SelectTrigger as _SelectTrigger, SelectValue as _SelectValue,
} from "../../components/ui/select";

const Select = _Select as any;
const SelectContent = _SelectContent as any;
const SelectItem = _SelectItem as any;
const SelectTrigger = _SelectTrigger as any;
const SelectValue = _SelectValue as any;

const ROW_H = 42;
const HOURS = Array.from({ length: 24 }, (_, h) => h);
const ALL = "__all__";

const hourLabel = (h: number) => {
  if (h === 0) return "12 AM";
  if (h < 12) return `${h} AM`;
  if (h === 12) return "12 PM";
  return `${h - 12} PM`;
};

type CalendarView = "Month" | "Week" | "Day" | "Agenda";

export function CalendarPage() {
  const cache = useQueryClient();
  const navigate = useNavigate();
  const appointmentsQuery = useQuery({ queryKey: ["crm-appointments"], queryFn: crmApi.appointments, refetchInterval: 30000 });
  const leadsQuery = useQuery({ queryKey: ["crm-calendar-leads"], queryFn: crmApi.leads });
  const teamQuery = useQuery({ queryKey: ["crm-team"], queryFn: crmApi.team });

  const [viewDate, setViewDate] = useState(() => new Date());
  const [view, setView] = usePersistentState<CalendarView>("crm-calendar:view", "Month");
  const [agentFilter, setAgentFilter] = useState(ALL);
  
  const [createOpen, setCreateOpen] = useState(false);
  const [createLeadId, setCreateLeadId] = useState("");
  const [createDue, setCreateDue] = useState("");
  const [createNote, setCreateNote] = useState("");
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState("");
  const [now, setNow] = useState(() => new Date());
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = window.setInterval(() => setNow(new Date()), 60000);
    return () => window.clearInterval(t);
  }, []);

  const appointments = appointmentsQuery.data ?? [];
  const leads = leadsQuery.data ?? [];
  const leadById = useMemo(() => new Map((leadsQuery.data ?? []).map((l: Lead) => [l.id, l])), [leadsQuery.data]);

  // Days range for Week / Day view
  const weekDays = useMemo(() => {
    if (view === "Day") return [startOfDay(viewDate)];
    const start = startOfWeek(viewDate, { weekStartsOn: 0 });
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  }, [viewDate, view]);

  // Days for Month view
  const monthDays = useMemo(() => {
    const mStart = startOfMonth(viewDate);
    const mEnd = endOfMonth(viewDate);
    const start = startOfWeek(mStart, { weekStartsOn: 0 });
    const end = endOfWeek(mEnd, { weekStartsOn: 0 });
    return eachDayOfInterval({ start, end });
  }, [viewDate]);

  // Filtered appointments by agent
  const visible = useMemo(() => {
    const all = appointmentsQuery.data ?? [];
    return all.filter((a: CrmAppointment) => {
      if (agentFilter !== ALL) {
        const lead = leadById.get(a.leadId);
        if ((lead?.assignedAgentId || "unassigned") !== agentFilter) return false;
      }
      return true;
    });
  }, [appointmentsQuery.data, agentFilter, leadById]);

  const forDay = (day: Date) => visible.filter((a: CrmAppointment) => isSameDay(new Date(a.dueAt), day));

  // Navigation shifting
  const shift = (dir: 1 | -1) => {
    if (view === "Month") setViewDate(d => addMonths(d, dir));
    else if (view === "Week") setViewDate(d => addWeeks(d, dir));
    else setViewDate(d => addDays(d, dir));
  };

  // Scroll week/day view into realistic daytime hours
  useEffect(() => {
    if (view !== "Week" && view !== "Day") return;
    const el = scrollRef.current;
    if (!el) return;
    const isCurrentWeek = weekDays.some(d => isSameDay(d, now));
    const targetHour = isCurrentWeek && now.getHours() >= 7 && now.getHours() <= 20
      ? Math.max(0, now.getHours() - 1)
      : 8; // Default to 8 AM rather than 12 AM midnight
    el.scrollTop = targetHour * ROW_H;
  }, [view, weekDays, now]);

  const nowTop = (now.getHours() + now.getMinutes() / 60) * ROW_H;
  const showNow = (view === "Week" || view === "Day") && weekDays.some(d => isSameDay(d, now));

  // Header Title
  const title = useMemo(() => {
    if (view === "Month") return format(viewDate, "MMMM yyyy");
    if (view === "Day") return format(viewDate, "EEEE, MMMM d, yyyy");
    if (view === "Agenda") return format(viewDate, "MMMM yyyy");
    // Week: handle month span
    const startM = format(weekDays[0], "MMM");
    const endM = format(weekDays[6], "MMM");
    if (startM === endM) {
      return `${format(weekDays[0], "MMMM")} ${format(weekDays[0], "d")} – ${format(weekDays[6], "d, yyyy")}`;
    }
    return `${startM} ${format(weekDays[0], "d")} – ${endM} ${format(weekDays[6], "d, yyyy")}`;
  }, [view, viewDate, weekDays]);

  const book = async () => {
    if (createBusy) return;
    if (!createLeadId) { setCreateError("Choose a lead."); return; }
    if (!createDue) { setCreateError("Pick a date and time."); return; }
    if (new Date(createDue).getTime() <= Date.now()) { setCreateError("Choose a future time."); return; }
    setCreateBusy(true); setCreateError("");
    try {
      await crmApi.createAppointment({ leadId: createLeadId, dueAt: new Date(createDue).toISOString(), note: createNote.trim() || undefined });
      await cache.invalidateQueries({ queryKey: ["crm-appointments"] });
      setCreateOpen(false); setCreateLeadId(""); setCreateDue(""); setCreateNote("");
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : "Unable to create appointment.");
    } finally {
      setCreateBusy(false);
    }
  };

  const openCreateForDate = (date: Date) => {
    const d = new Date(date);
    d.setHours(10, 0, 0, 0);
    const localIso = new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    setCreateDue(localIso);
    setCreateOpen(true);
  };

  return (
    <div className="flex h-full flex-col p-4 sm:p-5 md:p-6 overflow-hidden">
      {/* ── Top Bar: Title & Action (Clean, compact) ── */}
      <div className="flex flex-wrap items-center justify-between gap-3 shrink-0">
        <div className="flex items-center gap-2.5">
          <h1 className="text-sm font-semibold text-ink">Calendar</h1>
          <span className="font-mono text-xs rounded-full bg-soft px-2 py-0.5 text-ink-3">
            {visible.length} event{visible.length === 1 ? "" : "s"}
          </span>
        </div>
        <button 
          onClick={() => { setCreateError(""); setCreateOpen(true); }} 
          className="flex items-center gap-1.5 rounded-lg bg-brand px-3.5 py-1.5 text-xs font-semibold text-white hover:bg-brand-hover shadow-xs transition-colors"
        >
          <Plus className="h-3.5 w-3.5" /> Create
        </button>
      </div>

      {/* ── Toolbar: Date Navigation, View Selector, Agent Filter ── */}
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2.5 shrink-0">
        <div className="flex items-center gap-2">
          <div className="flex items-center rounded-lg border border-line bg-elevated shadow-xs">
            <button 
              onClick={() => shift(-1)} 
              aria-label="Previous" 
              className="flex h-7 w-7 items-center justify-center text-ink-3 hover:bg-soft hover:text-ink rounded-l-lg transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button 
              onClick={() => setViewDate(new Date())} 
              className="px-2.5 py-1 text-xs font-medium text-ink-2 hover:bg-soft hover:text-ink transition-colors border-x border-line/60"
            >
              Today
            </button>
            <button 
              onClick={() => shift(1)} 
              aria-label="Next" 
              className="flex h-7 w-7 items-center justify-center text-ink-3 hover:bg-soft hover:text-ink rounded-r-lg transition-colors"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <span className="text-sm font-semibold text-ink pl-1">{title}</span>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Segmented View Switcher: Month, Week, Day, Agenda */}
          <div className="flex items-center rounded-lg border border-line bg-soft p-0.5">
            {(["Month", "Week", "Day", "Agenda"] as CalendarView[]).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setView(v)}
                className={cn(
                  "px-2.5 py-1 text-xs font-medium rounded-md transition-colors",
                  view === v
                    ? "bg-elevated text-brand font-semibold shadow-xs"
                    : "text-ink-3 hover:text-ink"
                )}
              >
                {v}
              </button>
            ))}
          </div>

          {/* Agent Filter */}
          <Select value={agentFilter} onValueChange={setAgentFilter}>
            <SelectTrigger className="w-[140px] border-line bg-elevated text-xs text-ink-2 h-8">
              <SelectValue placeholder="All agents" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL} className="text-xs">All agents</SelectItem>
              <SelectItem value="unassigned" className="text-xs">Unassigned</SelectItem>
              {(teamQuery.data?.members ?? []).map((m: any) => (
                <SelectItem key={m.id} value={m.id} className="text-xs">{m.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {appointmentsQuery.error && (
        <p role="alert" className="mt-2 text-xs text-red-600">
          {(appointmentsQuery.error as Error).message}
        </p>
      )}

      {/* ── Calendar Main View Container ───────────────────────── */}
      <div className="mt-3 min-h-0 flex-1 rounded-xl border border-line bg-elevated shadow-xs overflow-hidden flex flex-col">
        {/* ── 1. MONTH VIEW (Balanced, non-empty, default) ──────── */}
        {view === "Month" && (
          <div className="flex flex-col h-full overflow-y-auto">
            {/* Day name headers */}
            <div className="grid grid-cols-7 border-b border-line bg-soft/60 text-center py-2 text-[11px] font-semibold text-ink-3 uppercase tracking-wider">
              <span>Sun</span><span>Mon</span><span>Tue</span><span>Wed</span><span>Thu</span><span>Fri</span><span>Sat</span>
            </div>
            {/* Month grid */}
            <div className="grid grid-cols-7 flex-1 auto-rows-fr divide-x divide-y divide-line/60">
              {monthDays.map((day) => {
                const dayEvents = forDay(day);
                const isCurrentMonth = isSameMonth(day, viewDate);
                const isToday = isSameDay(day, now);
                return (
                  <div
                    key={day.toISOString()}
                    onClick={() => openCreateForDate(day)}
                    className={cn(
                      "group min-h-[85px] sm:min-h-[105px] p-1.5 flex flex-col justify-between hover:bg-soft/50 cursor-pointer transition-colors",
                      !isCurrentMonth && "bg-soft/20 text-ink-4 opacity-55"
                    )}
                  >
                    {/* Date label header */}
                    <div className="flex items-center justify-between">
                      <span
                        className={cn(
                          "flex h-5 w-5 items-center justify-center rounded-full text-xs font-semibold",
                          isToday 
                            ? "bg-brand text-white" 
                            : isCurrentMonth ? "text-ink" : "text-ink-4"
                        )}
                      >
                        {format(day, "d")}
                      </span>
                      {dayEvents.length > 0 && (
                        <span className="text-[10px] font-mono text-ink-3">
                          {dayEvents.length}
                        </span>
                      )}
                    </div>

                    {/* Events list in date box */}
                    <div className="mt-1 space-y-1 flex-1 overflow-hidden">
                      {dayEvents.slice(0, 3).map((a) => {
                        const lead = leadById.get(a.leadId);
                        const d = new Date(a.dueAt);
                        return (
                          <div
                            key={a.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/crm/leads/${a.leadId}`);
                            }}
                            title={`${format(d, "h:mm a")} · ${a.note || lead?.name || "Appointment"}`}
                            className="flex items-center gap-1 rounded bg-brand/10 border border-brand/20 px-1.5 py-0.5 text-[11px] font-medium text-brand hover:bg-brand/20 transition-colors truncate"
                          >
                            <span className="font-mono text-[10px] opacity-75 shrink-0">{format(d, "h:mm a")}</span>
                            <span className="truncate">{a.note || lead?.name || "Meeting"}</span>
                          </div>
                        );
                      })}
                      {dayEvents.length > 3 && (
                        <p className="text-[10px] font-medium text-ink-3 pl-1">
                          +{dayEvents.length - 3} more
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── 2. WEEK & DAY VIEW (Refined, no Sat 3 3 bug, daytime focused) ── */}
        {(view === "Week" || view === "Day") && (
          <div ref={scrollRef} className="flex-1 overflow-auto">
            <div style={{ minWidth: view === "Day" ? "100%" : 7 * 130 + 56 }}>
              {/* Day headers */}
              <div 
                className="grid sticky top-0 z-20 border-b border-line bg-elevated"
                style={{ gridTemplateColumns: `56px repeat(${weekDays.length}, minmax(0, 1fr))` }}
              >
                <div className="border-r border-line bg-soft/30 flex items-center justify-center text-[10px] text-ink-3 uppercase font-mono">
                  GMT
                </div>
                {weekDays.map((day) => {
                  const isToday = isSameDay(day, now);
                  return (
                    <div
                      key={day.toISOString()}
                      className={cn(
                        "flex flex-col items-center justify-center py-2 px-1 border-l border-line text-xs transition-colors",
                        isToday && "bg-brand/5"
                      )}
                    >
                      <span className="text-[10px] font-medium text-ink-3 uppercase tracking-wider">
                        {format(day, "EEE")}
                      </span>
                      <span
                        className={cn(
                          "mt-0.5 flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold",
                          isToday ? "bg-brand text-white shadow-xs" : "text-ink"
                        )}
                      >
                        {format(day, "d")}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Hour Grid */}
              <div 
                className="relative grid" 
                style={{ gridTemplateColumns: `56px repeat(${weekDays.length}, minmax(0, 1fr))` }}
              >
                {/* Time markers column */}
                <div className="border-r border-line bg-soft/10">
                  {HOURS.map((h) => (
                    <div 
                      key={h} 
                      style={{ height: ROW_H }} 
                      className="relative border-b border-line/40 pr-2 text-right text-[10px] font-mono text-ink-3 select-none flex items-start justify-end pt-1"
                    >
                      <span>{hourLabel(h)}</span>
                    </div>
                  ))}
                </div>

                {/* Day Columns */}
                {weekDays.map((day) => {
                  const isToday = isSameDay(day, now);
                  const dayEvents = forDay(day);
                  return (
                    <div
                      key={day.toISOString()}
                      className={cn(
                        "relative border-l border-line/60 first:border-l-0",
                        isToday && "bg-brand/[0.015]"
                      )}
                      style={{
                        height: 24 * ROW_H,
                        backgroundImage: `repeating-linear-gradient(to bottom, transparent 0, transparent ${ROW_H - 1}px, var(--line, #e5e7eb) ${ROW_H - 1}px, var(--line, #e5e7eb) ${ROW_H}px)`,
                      }}
                      onDoubleClick={() => openCreateForDate(day)}
                    >
                      {dayEvents.map((a: CrmAppointment) => {
                        const d = new Date(a.dueAt);
                        const top = (d.getHours() + d.getMinutes() / 60) * ROW_H;
                        const lead = leadById.get(a.leadId);
                        return (
                          <button
                            key={a.id}
                            onClick={() => navigate(`/crm/leads/${a.leadId}`)}
                            title={`${format(d, "h:mm a")} · ${a.note || lead?.name || "Appointment"}`}
                            style={{ top: Math.max(0, top + 1) }}
                            className="absolute left-1 right-1 flex flex-col gap-0.5 overflow-hidden rounded-md border-l-3 border-brand bg-brand-soft/90 px-2 py-1 text-left shadow-xs hover:bg-brand/20 transition-all z-10"
                          >
                            <span className="font-mono text-[10px] font-bold text-brand leading-none">
                              {format(d, "h:mm a")}
                            </span>
                            <span className="truncate text-xs font-semibold text-ink leading-tight">
                              {a.note || lead?.name || "Appointment"}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  );
                })}

                {/* Current Time Red Line */}
                {showNow && (
                  <div 
                    className="pointer-events-none absolute left-0 right-0 z-20 flex items-center" 
                    style={{ top: nowTop }}
                  >
                    <div className="h-2 w-2 rounded-full bg-red-500 -ml-1" />
                    <div className="ml-14 flex-1 h-[2px] bg-red-500/80 shadow-xs" />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── 3. AGENDA / LIST VIEW (Clean upcoming schedule) ───── */}
        {view === "Agenda" && (
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {visible.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center text-ink-3 space-y-2">
                <CalendarDays className="h-8 w-8 text-ink-4" />
                <p className="text-sm font-semibold text-ink">No upcoming appointments</p>
                <p className="text-xs text-ink-3">Click &quot;Create&quot; to book a meeting or follow-up with any lead.</p>
              </div>
            ) : (
              <div className="max-w-3xl mx-auto space-y-3">
                {visible
                  .sort((a, b) => a.dueAt.localeCompare(b.dueAt))
                  .map((a) => {
                    const d = new Date(a.dueAt);
                    const lead = leadById.get(a.leadId);
                    const isToday = isSameDay(d, now);
                    return (
                      <div
                        key={a.id}
                        onClick={() => navigate(`/crm/leads/${a.leadId}`)}
                        className="group flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl border border-line bg-elevated p-3.5 hover:border-brand/40 hover:shadow-xs cursor-pointer transition-all"
                      >
                        <div className="flex items-start gap-3">
                          <div className={cn(
                            "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border text-xs font-semibold",
                            isToday ? "bg-brand text-white border-brand" : "bg-soft border-line text-ink"
                          )}>
                            {format(d, "d")}
                          </div>
                          <div className="space-y-0.5">
                            <div className="flex items-center gap-2">
                              <h4 className="text-xs font-semibold text-ink group-hover:text-brand transition-colors">
                                {a.note || "Scheduled Meeting"}
                              </h4>
                              {isToday && (
                                <span className="rounded bg-brand/10 px-1.5 py-0.2 text-[10px] font-semibold text-brand">
                                  Today
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-3 text-xs text-ink-3">
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                <span>{format(d, "EEE, MMM d · h:mm a")}</span>
                              </span>
                              {lead && (
                                <span className="flex items-center gap-1">
                                  <User className="h-3 w-3" />
                                  <span>{lead.name}</span>
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 justify-end">
                          <span className="rounded-full bg-soft px-2.5 py-1 text-[11px] font-medium text-ink-2">
                            {a.status || "Scheduled"}
                          </span>
                          <ExternalLink className="h-3.5 w-3.5 text-ink-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Appointment Creation Modal ──────────────────────────── */}
      {createOpen && (
        <Modal
          title="Create appointment"
          subtitle="Book time on any lead."
          onClose={() => !createBusy && setCreateOpen(false)}
          width="max-w-md"
          footer={
            <>
              <button 
                disabled={createBusy} 
                onClick={() => setCreateOpen(false)} 
                className="rounded-lg px-4 py-2 text-xs font-semibold text-ink-2 hover:bg-soft transition-colors"
              >
                Cancel
              </button>
              <button 
                disabled={createBusy} 
                onClick={() => void book()} 
                className="rounded-lg bg-brand px-4 py-2 text-xs font-semibold text-white hover:bg-brand-hover disabled:opacity-50 transition-colors shadow-xs"
              >
                {createBusy ? "Saving…" : "Create"}
              </button>
            </>
          }
        >
          <div className="space-y-4">
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-ink-2">Lead</label>
              <Select value={createLeadId} onValueChange={(v: string) => { setCreateLeadId(v); setCreateError(""); }}>
                <SelectTrigger className="w-full border-line bg-transparent px-3 py-2 text-xs text-ink h-9">
                  <SelectValue placeholder="Choose a lead" />
                </SelectTrigger>
                <SelectContent>
                  {leads.map((l: Lead) => (
                    <SelectItem key={l.id} value={l.id} className="text-xs focus:bg-soft focus:text-ink">
                      {[l.name, l.phone].filter(Boolean).join(" · ") || l.contactId || l.id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-ink-2">Date &amp; time</label>
              <DateTimePicker 
                value={createDue} 
                onChange={val => { setCreateDue(val); setCreateError(""); }} 
                placeholder="Pick date & time"
                className="w-full" 
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-ink-2">Agenda (optional)</label>
              <textarea 
                value={createNote} 
                onChange={e => setCreateNote(e.target.value)} 
                placeholder="e.g. Product demo, Discovery call..." 
                rows={3} 
                maxLength={2000} 
                className="w-full resize-none rounded-lg border border-line bg-transparent px-3 py-2 text-xs text-ink outline-none focus:border-brand" 
              />
            </div>
            {createError && <p role="alert" className="text-xs font-semibold text-red-500">{createError}</p>}
          </div>
        </Modal>
      )}
    </div>
  );
}
